import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useComponentMount } from 'utilities/hooks.util';
import { useFormik } from 'formik';
import { AutoRenewForm } from 'components';
import { AutoRenewFormValues } from 'types';
import { EditEnquiryAutoRenewSchema } from 'utilities/validation/editEnquiryAutoRenew.validation';

import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import { getEnquiryAutoRenewPropertiesStart } from './localStore/actions';
import {
  calculateRenewalsLeft,
  calculateDayLeft,
  calculateNextRenewal
} from './utils/autoRenewCalculations';
import { getNewDate } from 'utilities/helper.util';

const AutoRenew: React.FC<Props> = ({
  autoRenewData,
  onClose,
  onSaveAutoRenew,
  calendarRef,
  editingEnquiryAutoRenew,
  enquiryAutoRenew
}) => {
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { enquiryAutoRenewProperties, fetchingEnquiryAutoRenewProperties } =
    localStore;
  const {
    isAnonymousView,
    enquiryId,
    isExpired,
    isNewAutoRenew = false
  } = autoRenewData;
  const [enquiryAutoRenewPropertiesData, setEnquiryAutoRenewPropertiesData] =
    useState<any>('');
  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);
  const [newToAutoRenew, setNewToAutoRenew] = useState<boolean>(isNewAutoRenew);
  const needCloseForm = useRef<boolean>(false);
  const [recalculateSummary, setRecalculateSummary] = useState<boolean>(false);

  const formInitialValues = {
    autoRenew: true,
    autoRenewEvery: 0,
    autoRenewUntil: '',
    jobEndDate: ''
  };

  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleSubmit,
    setFieldTouched
  } = useFormik<AutoRenewFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema: EditEnquiryAutoRenewSchema,
    onSubmit: (values: AutoRenewFormValues) => {
      if (!values.autoRenew) {
        needCloseForm.current = true;
      }
      setRecalculateSummary(true);
      onSaveAutoRenew(values);
    }
  });

  const recalculateARSummary = useCallback(
    (values: any) => {
      let autoRenewDate = autoRenewData.autoRenewDate;

      /*
        For first time non AR enquiry that will be AR enabled,
        the autoRenewDate is null, so need to calculate it.
        If not, the calculation will be wrong.
      */
      if (!autoRenewDate) {
        autoRenewDate = getNewDate(
          enquiryAutoRenewPropertiesData?.enquiryLodgedAt
        )
          .add(28, 'day')
          .subtract(values.autoRenewEvery, 'day')
          .format('YYYY-MM-DD');
      }

      const calculationData = {
        auto_renew_date: autoRenewDate,
        auto_renew_until: values.autoRenewUntil,
        internal_create: autoRenewData.internalCreate,
        auto_renew_every: values.autoRenewEvery
      };
      const renewalsLeft = calculateRenewalsLeft(calculationData);
      const renews = calculateDayLeft(calculationData);
      const nextRenewal = calculateNextRenewal(calculationData, renewalsLeft);
      const newData = {
        ...enquiryAutoRenewPropertiesData,
        renewalsLeft,
        renews,
        nextRenewal
      };
      setEnquiryAutoRenewPropertiesData(newData);
      if (values.autoRenew && isNewAutoRenew) {
        setNewToAutoRenew(false);
      }
    },
    [autoRenewData, enquiryAutoRenewPropertiesData, isNewAutoRenew]
  );

  useEffect(() => {
    if (recalculateSummary && savedSuccess) {
      recalculateARSummary(values);
      setRecalculateSummary(false);
    }
  }, [recalculateSummary, savedSuccess, values, recalculateARSummary]);

  useEffect(() => {
    if (enquiryAutoRenew !== undefined && !fetchingEnquiryAutoRenewProperties) {
      setSavedSuccess(true);
    }
    setTimeout(() => {
      needCloseForm.current ? onClose() : setSavedSuccess(false);
    }, 2000);
  }, [enquiryAutoRenew, fetchingEnquiryAutoRenewProperties, onClose]);

  useEffect(() => {
    const {
      internalCreate,
      isArchived,
      isAnonymousView,
      jobStartAt,
      jobEndAt,
      enquiryId,
      isExpired,
      autoRenewDone,
      isEdit,
      enquiryLodgedAt
    } = autoRenewData;

    if (
      !isAnonymousView &&
      enquiryAutoRenewProperties &&
      !fetchingEnquiryAutoRenewProperties
    ) {
      setFieldValue('autoRenew', enquiryAutoRenewProperties.auto_renew);
      setFieldValue(
        'autoRenewEvery',
        enquiryAutoRenewProperties.auto_renew_every
      );
      const jobEndDate = jobEndAt;
      setFieldValue('jobEndDate', jobEndDate);
      setFieldValue(
        'autoRenewUntil',
        enquiryAutoRenewProperties.auto_renew_until
      );

      const renewalsLeft = calculateRenewalsLeft(enquiryAutoRenewProperties);
      const renews = calculateDayLeft(enquiryAutoRenewProperties);
      const nextRenewal = calculateNextRenewal(
        enquiryAutoRenewProperties,
        renewalsLeft
      );

      let isEditable = false;
      if (
        !isExpired &&
        !isAnonymousView &&
        internalCreate &&
        !autoRenewDone &&
        !isArchived &&
        renewalsLeft > 0
      ) {
        isEditable = true;
      }

      if (isNewAutoRenew) {
        isEditable = true;
      }

      const filteredData = {
        jobStartAt,
        jobEndAt,
        isAnonymousView,
        autoRenew: enquiryAutoRenewProperties.auto_renew,
        autoRenewEvery: enquiryAutoRenewProperties.auto_renew_every,
        autoRenewUntil: enquiryAutoRenewProperties.auto_renew_until,
        internalCreate: enquiryAutoRenewProperties.internal_create,
        renewalsLeft,
        renews,
        nextRenewal,
        isEditable,
        enquiryId,
        isEdit,
        enquiryLodgedAt
      };
      setEnquiryAutoRenewPropertiesData(filteredData);
    } else if (isAnonymousView && !fetchingEnquiryAutoRenewProperties) {
      const {
        autoRenew,
        autoRenewEvery,
        autoRenewUntil,
        internalCreate,
        autoRenewDate
      } = autoRenewData;

      const calculationData = {
        auto_renew_date: autoRenewDate,
        auto_renew_until: autoRenewUntil,
        internal_create: internalCreate,
        auto_renew_every: autoRenewEvery
      };

      const renewalsLeft = calculateRenewalsLeft(calculationData);
      const renews = calculateDayLeft(calculationData);
      const nextRenewal = calculateNextRenewal(calculationData, renewalsLeft);

      let isEditable = false;
      if (
        !isExpired &&
        !isAnonymousView &&
        internalCreate &&
        !autoRenewDone &&
        !isArchived &&
        renewalsLeft > 0
      ) {
        isEditable = true;
      }

      const filteredData = {
        jobStartAt,
        jobEndAt,
        isAnonymousView,
        autoRenew: autoRenew,
        autoRenewEvery: autoRenewEvery,
        autoRenewUntil: autoRenewUntil,
        internalCreate: internalCreate,
        renewalsLeft,
        renews,
        nextRenewal,
        isEditable,
        enquiryId
      };
      setEnquiryAutoRenewPropertiesData(filteredData);
    }
  }, [
    enquiryAutoRenewProperties,
    fetchingEnquiryAutoRenewProperties,
    autoRenewData,
    setFieldValue,
    isNewAutoRenew
  ]);

  useEffect(() => {
    if (values.jobEndDate) {
      setFieldTouched('jobEndDate', true);
    } else if (values.autoRenewUntil) {
      setFieldTouched('autoRenewUntil', true);
    }
  }, [values, setFieldTouched]);

  useComponentMount(() => {
    const params = { enquiryId: enquiryId };
    !isAnonymousView &&
      localDispatch(getEnquiryAutoRenewPropertiesStart(params));
  });

  return (
    <AutoRenewForm
      touched={touched}
      errors={errors}
      values={values}
      enquiryAutoRenewPropertiesData={enquiryAutoRenewPropertiesData}
      fetchingEnquiryAutoRenewProperties={fetchingEnquiryAutoRenewProperties}
      handleSubmit={handleSubmit}
      setFieldValue={setFieldValue}
      setFieldTouched={setFieldTouched}
      calendarRef={calendarRef}
      isAnonymousView={isAnonymousView}
      onClose={onClose}
      editingEnquiryAutoRenew={editingEnquiryAutoRenew}
      savedSuccess={savedSuccess}
      isExpired={isExpired}
      isNewAutoRenew={newToAutoRenew}
    />
  );
};

type Props = {
  autoRenewData: any;
  onClose: Function;
  onSaveAutoRenew: Function;
  calendarRef: any;
  editingEnquiryAutoRenew: boolean;
  enquiryAutoRenew: any;
};

export default React.memo(AutoRenew);
